import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import {
  type RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { theme, useResponsive } from '@sg/garnish';

import {
  GiftCard,
  useRedeemGiftCardDefaultValues,
} from '@order/features/gift-card';
import { useTelemetry } from '@order/Telemetry';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
} from '../../components';
import { type AccountTabStackParamList } from '../../navigation/AppNavigation.props';
import {
  useGiftCardBalance,
  useGiftCardContent,
  useGiftCardOrders,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountGiftCardsScreen = () => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const navigation = useNavigation();
  const { track } = useTelemetry();

  // ─── Queries ───────────────────────────────────────────────────────────────

  const giftCardBalanceData = useGiftCardBalance();
  const giftCardOrdersData = useGiftCardOrders();
  const contentfulData = useGiftCardContent();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handleOnRequestGiftCardPurchase = useCallback(() => {
    track('account.gift.header.pressed');
    navigation.navigate('MainTabs', {
      screen: 'GiftCardsTab',
      params: { screen: 'GiftCards' },
    });
  }, [track, navigation]);

  const handleOnRequestGiftCardRedemption = useCallback(() => {
    track('account.gift.redemption.pressed');
    navigation.navigate('Modal', { screen: 'AddGiftCard' });
  }, [track, navigation]);

  const getConfirmationLink = useCallback((id: string) => {
    return {
      screen: 'MainTabs' as const,
      params: {
        screen: 'GiftCardsTab' as const,
        params: {
          screen: 'GiftCardConfirmation' as const,
          params: { orderId: id, referrer: 'gift-card-orders' as const },
          initial: false,
        },
      },
    };
  }, []);

  // ─── Gift Cards ────────────────────────────────────────────────────────────

  const { params } =
    useRoute<RouteProp<AccountTabStackParamList, 'GiftCards'>>();
  const giftCardRedemptionEncodedData = params?.giftCardId;

  const defaultRedemptionValues = useRedeemGiftCardDefaultValues({
    giftCardRedemptionEncodedData,
  });

  // ─── Rendering ─────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer screen="gift-cards">
      {currentBreakpoint.isXS ? (
        <AccountScreenTitle title={formatMessage(messages.title)} />
      ) : null}

      <AccountScreenSection style={styles.content}>
        <GiftCard.Account.Header
          {...contentfulData}
          onPress={handleOnRequestGiftCardPurchase}
        />

        <GiftCard.Account.Info
          {...giftCardBalanceData}
          {...defaultRedemptionValues}
          onRedeemGiftCardPress={handleOnRequestGiftCardRedemption}
        />

        <GiftCard.Account.Orders
          {...giftCardOrdersData}
          onOpenGiftCardsScreen={handleOnRequestGiftCardPurchase}
          getConfirmationLink={getConfirmationLink}
        />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    gap: theme.spacing['10'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Gift Cards',
    description: 'Gift Cards Screen | Title',
  },
});
